<template>
  <div id="header-links" class="hidden xl:flex relative ml-auto items-baseline gap-4">
    <router-link
      v-if="!isSettingViewOnly"
      :to="{ name: 'reservationsDashboard' }"
      id="venue-dashboard"
      class="link text-fg-link-primary block text-base font-normal"
    >
      Dashboard
    </router-link>
    <router-link
      v-if="settingStore.flagEnabled(FeatureFlag.CONTINUOUS_SERVICE)"
      :to="{ name: CS_DATE_ROUTE_NAMES.ROOT }"
      id="continuous-service"
      class="link text-fg-link-primary block text-base font-normal"
    >
      Continuous Service
    </router-link>
    <PerformanceToolsLink
      v-if="!isSettingViewOnly"
      id="venue-performance-tools"
      class="link text-fg-link-primary block text-base font-normal"
    >
      Performance Tools
    </PerformanceToolsLink>
    <router-link
      v-if="!isSettingViewOnly"
      :to="{ name: RESIDENCY_ROUTE_NAMES.LIST }"
      id="venue-residencies"
      class="link text-fg-link-primary block text-base font-normal"
    >
      Residencies
    </router-link>
    <router-link
      v-if="!isSettingViewOnly"
      :to="{ name: SHOW_ROUTE_NAMES.LIST }"
      id="venue-shows"
      class="link text-fg-link-primary block text-base font-normal"
    >
      Shows
    </router-link>
    <template v-if="isOwner">
      <router-link
        v-if="!isSettingViewOnly"
        :to="{ name: MAILCHIMP_ROUTE_NAMES.TEMPLATE_EDIT }"
        id="mailchimp-template-edit"
        class="link text-fg-link-primary block text-base font-normal"
      >
        Marketing
      </router-link>
      <router-link
        v-if="!isSettingViewOnly"
        :to="{ name: 'reportsOverview' }"
        id="venue-reports"
        class="link text-fg-link-primary block text-base font-normal"
      >
        Reports
      </router-link>
    </template>
    <SearchInput v-if="!isSettingViewOnly" ref="searchInput" />

    <OnClickOutside @trigger="closeModal" class="relative">
      <div
        v-if="isOpen"
        id="search-results-modal"
        class="w-700px max-h-[80vh] overflow-auto tt-scroll absolute z-20 translate-y-10 top-0 right-0 bg-surface-primary rounded border border-linear-subtle shadow-sm"
      >
        <div
          v-for="result in searchResults"
          :key="`result-${result.id}`"
          class="px-4 py-4 hover:bg-surface-base"
          @click="closeModal"
        >
          <SearchResult :result="result" />
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>

<script setup lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { storeToRefs } from 'pinia'
import { computed, ref, useTemplateRef, watch } from 'vue'

import SearchInput from '@/components/SearchInput.vue'
import SearchResult from '@/components/SearchResult.vue'
import { CS_DATE_ROUTE_NAMES } from '@/modules/csDate/constants/CSDateRouteNames'
import { MAILCHIMP_ROUTE_NAMES } from '@/modules/mailchimp/constants/MailchimpRouteNames'
import PerformanceToolsLink from '@/modules/performance/components/atoms/PerformanceToolsLink.vue'
import { RESIDENCY_ROUTE_NAMES } from '@/modules/residency/constants/ResidencyRouteNames'
import { SHOW_ROUTE_NAMES } from '@/modules/show/constants/ShowRouteName'
import { useGuestStore } from '@/stores/guest'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import { sortBy } from '@/utils/utils'

import { FeatureFlag, GuestSearchRead } from '@generated/types'

const settingStore = useSettingStore()
const { isSettingViewOnly } = storeToRefs(settingStore)

const userStore = useUserStore()
const { isOwner } = storeToRefs(userStore)

const guestStore = useGuestStore()
const searchResults = computed<GuestSearchRead[]>(() => {
  sortBy(guestStore.searchResults, 'last_name')
  return guestStore.searchResults
})

const input = useTemplateRef<InstanceType<typeof SearchInput>>('searchInput')
const isOpen = ref<boolean>(false)
watch(searchResults, newResults => {
  isOpen.value = newResults.length > 0
})
function closeModal() {
  isOpen.value = false
  input.value?.clear()
}
</script>
